import { isMobile } from "@/stores/MenuStore";
import { Season, RowValues } from "@/types";
import { Select, SelectItem, Checkbox, Input, Spacer } from "@heroui/react";
import { Tooltip } from "@heroui/tooltip";
import { debounce } from "lodash";
import { HelpCircle } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from 'react-i18next';

interface FilterWindowProps {
    seasons: ('all' | Season)[];
    windowSizes?: any[];
    leaguesNames?: any[];
    onChange?: (windowSize: string, season: string | null, league?: string[], homeOrAway?: string | null, bypass?: boolean) => void;
    startingWindowSize?: number;
    currentWindow: string;
    setCurrentWindow: (w: string) => void;
    season: string;
    setSeason: (s: string) => void;
    leagues: string[];
    setLeagues: (l: string[]) => void;
    home?: boolean;
    setHome?: (h: boolean) => void;
    away?: boolean;
    setAway?: (a: boolean) => void;
    activePlayers?: boolean;
    setActivePlayers?: (a: boolean) => void;
    selectedStat?: string;
    setSelectedStat?: (s: string) => void;
    currentHalf?: string;
    setCurrentHalf?: (h: string) => void;
    streakThreshold?: number;
    setStreakThreshold?: (t: number) => void;
    highlightValue?: number;
    setHighlightValue?: (h: number) => void;
    hideEmptyStats?: boolean;
    setHideEmptyStats?: (h: boolean) => void;
    hideCalculedColumns?: boolean;
    setHideCalculedColumns?: (h: boolean) => void;
    bypass?: boolean;
    sumSubstitutions?: boolean;
    setSumSubstitutions?: (s: boolean) => void;
    hideTotal?: boolean;
    setHideTotal?: (t: boolean) => void;
    typeTable: 'league' | 'team' | 'player';
    isPlayerModal: boolean;
    statsList?: RowValues[];
}

function getBestSeasonToShowFirst(seasons: ('all' | Season)[]): string {
    let countCurrentNames: string[] = [];
    let firstCurrent = '';
    let firstFinished = '';
    for (let i = 0; i < seasons.length; i++) {
        let season = seasons[i];
        if (season != 'all' && season.is_current) {
            if (!countCurrentNames.includes(season.name)) countCurrentNames.push(season.name);
            if (!firstCurrent) firstCurrent = season.name;
        }
        if (season != 'all' && season.finished && !firstFinished) firstFinished = season.name;
    }

    if (countCurrentNames.length != 1) return 'all';

    if (firstCurrent) return firstCurrent;
    if (firstFinished) return firstFinished;

    return 'all';
}

export default function FilterWindowTable({
    seasons,
    windowSizes,
    leaguesNames,
    onChange,
    startingWindowSize,
    currentWindow,
    setCurrentWindow,
    season,
    setSeason,
    leagues,
    setLeagues,
    home,
    setHome,
    away,
    setAway,
    activePlayers,
    setActivePlayers,
    selectedStat,
    setSelectedStat,
    currentHalf,
    setCurrentHalf,
    streakThreshold,
    setStreakThreshold,
    hideEmptyStats,
    setHideEmptyStats,
    hideCalculedColumns,
    setHideCalculedColumns,
    setHighlightValue,
    highlightValue,
    bypass,
    sumSubstitutions,
    setSumSubstitutions,
    hideTotal,
    setHideTotal,
    typeTable,
    isPlayerModal,
    statsList,
}: FilterWindowProps) {
    const { t } = useTranslation();
    const WINDOW_SIZES = windowSizes || [5, 10, 20, 30, 50, 'all'];

    const [dirty, setDirty] = useState(false);
    const [seasonsStartup, setSeasonsStartup] = useState(false);

    const homeOrAway = home && !away ? 'home' : !home && away ? 'away' : null;

    const seasonsNames = Array.from(new Set(seasons.map((season) => season == 'all' ? 'all' : season.name)));

    const isDisabledWindowItem = (itemName: string) => {
        if ((!season || season == 'all') && (itemName == WINDOW_SIZES[WINDOW_SIZES.length - 1].toString())) return true;
        return false;
    };

    useEffect(() => {
        if (!currentWindow || currentWindow === '') {
            setCurrentWindow(startingWindowSize?.toString() || WINDOW_SIZES[1].toString());
        }
    }, [season]);

    const debouncedOnChange = onChange ? useCallback(debounce(onChange, 30), []) : null;

    const mostRecentSeason = (!dirty && seasonsNames && seasonsNames.length > 1) ? getBestSeasonToShowFirst(seasons) : season;

    useEffect(() => {
        if (!dirty && seasonsNames && seasonsNames.length > 1) {
            setSeasonsStartup(true);
            setSeason(mostRecentSeason);
        }
    }, [seasonsNames]);

    useEffect(() => {
        if (isDisabledWindowItem(currentWindow)) {
            setCurrentWindow(WINDOW_SIZES[2].toString());
        }
        if (debouncedOnChange && seasonsStartup) {
            debouncedOnChange(
                currentWindow,
                mostRecentSeason != 'all'
                    ? seasons.filter((s) => s != 'all' && s.name == mostRecentSeason).map((s) => s != 'all' && s.id).join(',')
                    : null,
                leagues,
                homeOrAway,
                bypass
            );
        }
    }, [currentWindow, season, leagues, seasonsStartup, homeOrAway, bypass, seasonsNames.join('').length]);

    const halftimes = [
        { value: 'all', label: t('filter_window_table.filter_window_table_1') },
        { value: 'first-half', label: t('filter_window_table.filter_window_table_2') },
        { value: 'second-half', label: t('filter_window_table.filter_window_table_3') },
    ];

    return (
        <div className="flex w-full items-start justify-end flex-wrap gap-2 p-1 overflow-hidden">
            <div className="float-start fadeIn gap-2 mr-auto pl-2 mt-2">
                {(setSumSubstitutions && selectedStat != '' && !isPlayerModal) &&
                    <>

                        <Checkbox
                            isSelected={sumSubstitutions}
                            onChange={() => setSumSubstitutions(!sumSubstitutions)}
                            size="md"
                        >
                            <span className={`text-medium transition ${sumSubstitutions ? 'text-default-800' : 'text-default-400'}`}>{t('filter_window_table.filter_window_table_4')}</span>
                        </Checkbox>
                        <Tooltip content={t('filter_window_table.filter_window_table_5')} >
                            <HelpCircle className="text-xs ml-2 mb-2 mt-1 text-default-400 inline-block " size={14} />
                        </Tooltip>
                    </>
                }
                <Spacer />
                {(setActivePlayers && selectedStat != '' && !isPlayerModal) &&
                    <Checkbox
                        isSelected={activePlayers}
                        onChange={() => setActivePlayers(!activePlayers)}
                        size="md"
                    >
                        <Tooltip content={t('filter_window_table.filter_window_table_6')}>
                            <span className={`text-medium transition ${activePlayers ? 'text-default-800' : 'text-default-400'}`}>{t('filter_window_table.filter_window_table_7')}</span>
                        </Tooltip>
                    </Checkbox>
                }
                <Spacer />
                <div className="flex flex-wrap items-center gap-4">
                    {
                        setHideEmptyStats && !isPlayerModal &&
                        <Checkbox
                            isSelected={hideEmptyStats}
                            onChange={() => setHideEmptyStats(!hideEmptyStats)}
                            size="md"
                        >
                            <Tooltip content={t('filter_window_table.filter_window_table_8')}>
                                <span className={`text-medium transition ${hideEmptyStats ? 'text-default-800' : 'text-default-400'}`}>{t('filter_window_table.filter_window_table_9')}</span>
                            </Tooltip>
                        </Checkbox>
                    }
                    {(setHideTotal && typeTable == 'team' && !isPlayerModal) &&
                        <Checkbox
                            isSelected={hideTotal}
                            onChange={() => setHideTotal(!hideTotal)}
                            size="md"
                        >
                            <span className={`text-medium transition ${hideTotal ? 'text-default-800' : 'text-default-400'}`}>{t('filter_window_table.filter_window_table_24')}</span>
                        </Checkbox>
                    }
                    {(setHideCalculedColumns && isMobile() && !isPlayerModal) &&
                        <Checkbox
                            isSelected={hideCalculedColumns}
                            onChange={() => setHideCalculedColumns(!hideCalculedColumns)}
                            size="md"
                        >
                            <span className={`text-medium transition ${hideCalculedColumns ? 'text-default-800' : 'text-default-400'}`}>{t('filter_window_table.filter_window_table_25')}</span>
                        </Checkbox>
                    }
                </div>
            </div>
            {setStreakThreshold && (streakThreshold || streakThreshold == 0 && !isPlayerModal) &&
                <Input size="sm" variant="underlined" min={0} label={t('filter_window_table.filter_window_table_10')} type="number" value={streakThreshold.toString()} onChange={(e) => setStreakThreshold(!e.target.value ? 0 : parseInt(e.target.value))}
                    className="w-full  max-w-[80px] shadow-md rounded-medium"
                />}
            {setHighlightValue && (highlightValue || highlightValue == 0 && !isPlayerModal) &&
                <Input size="sm" variant="underlined" min={0} label={t('filter_window_table.filter_window_table_11')} type="number" value={highlightValue.toString()} onChange={(e) => setHighlightValue(!e.target.value ? 0 : parseInt(e.target.value))}
                    className="w-full  max-w-[80px] shadow-md rounded-medium"
                />}
            {(setHome && setAway) &&
                <div className="flex fadeIn gap-2 flex-col min-w-[160px] w-full md:max-w-[200px] pl-2">
                    <Checkbox
                        isSelected={home}
                        onChange={() => home ? (setHome(!home), setAway(true)) : setHome(!home)}
                        size="md"
                    >
                        <span className={`text-medium transition ${home ? 'text-default-800' : 'text-default-400'}`}>{t('filter_window_table.filter_window_table_12')}</span>
                    </Checkbox>
                    <Checkbox
                        isSelected={away}
                        onChange={() => away ? (setAway(!away), setHome(true)) : setAway(!away)}
                        size="md"
                    >
                        <span className={`text-medium ${away ? 'text-default-800' : 'text-default-400'}`}>{t('filter_window_table.filter_window_table_13')}</span>
                    </Checkbox>
                </div>
            }
            {(setSelectedStat) && (
                <Select
                    selectedKeys={[selectedStat ? selectedStat : '']}
                    onChange={(e) => setSelectedStat((e.target.value))}
                    items={[
                    { value: '', label: t('filter_window_table.filter_window_table_21') },
                        ...(
                            (typeTable !== 'player'
                                ? statsList?.filter(stat => stat.onClick)
                                : statsList
                            )?.map(stat => ({
                                value: stat.id,
                                label: t(stat.name),
                            })) || []
                        )
                    ]}
                    label={t('filter_window_table.filter_window_table_28')}
                    placeholder={t('filter_window_table.filter_window_table_29')}
                    className="min-w-[160px] w-full md:max-w-[200px] shadow-md rounded-medium"
                >   
                    {(item) => <SelectItem key={item.value}>{item.label}</SelectItem>}
                </Select>
            )}
            {(setCurrentHalf && selectedStat == '' && !isPlayerModal) && (
                <Select
                    selectedKeys={[currentHalf == null ? 'all' : currentHalf]}
                    onChange={(e) => e.target.value && setCurrentHalf((e.target.value))}
                    items={halftimes}
                    label={t('filter_window_table.filter_window_table_14')}
                    placeholder={t('filter_window_table.filter_window_table_15')}
                    className="min-w-[160px] w-full md:max-w-[200px] shadow-md rounded-medium"
                >
                    {(item) => <SelectItem key={item.value == null ? 'None' : item.value}>{item.label}</SelectItem>}
                </Select>
            )}
            <Select
                selectedKeys={[season]}
                onChange={(e) => e.target.value && setDirty(true) || setSeason((e.target.value))}
                items={seasonsNames.map((season) => ({ value: season, label: season.toString() }))}
                label={t('filter_window_table.filter_window_table_16')}
                placeholder={t('filter_window_table.filter_window_table_17')}
                className="min-w-[160px] w-full md:max-w-[200px] shadow-md rounded-medium"
            >
                {(item) => <SelectItem key={item.value}>{item.label == 'all' ? t('filter_window_table.filter_window_table_18') : item.label}</SelectItem>}
            </Select>
            <Select
                selectedKeys={[currentWindow]}
                onChange={(e) => e.target.value && setDirty(true) || setCurrentWindow(e.target.value)}
                items={WINDOW_SIZES.map((value) => ({ value, label: value.toString() }))}
                label={t('filter_window_table.filter_window_table_19')}
                placeholder={t('filter_window_table.filter_window_table_20')}
                className="min-w-[160px] w-full md:max-w-[200px] shadow-md rounded-medium"
            >
                {(wind) => <SelectItem isDisabled={isDisabledWindowItem(wind.label)} key={wind.value}>{wind.label == 'all' ? t('filter_window_table.filter_window_table_21') : wind.label}</SelectItem>}
            </Select>
            {(leaguesNames) &&
                <Select
                    selectedKeys={leagues}
                    onSelectionChange={(selected) => {
                        setDirty(true);
                        setLeagues(Array.from(selected as Iterable<string>));
                    }}
                    items={leaguesNames}
                    label={t('filter_window_table.filter_window_table_22')}
                    placeholder={t('filter_window_table.filter_window_table_23')}
                    className="min-w-[160px] w-full md:max-w-[200px] shadow-md rounded-medium"
                    selectionMode="multiple"
                >
                    {(wind) => <SelectItem key={wind.value}>{wind.label}</SelectItem>}
                </Select>
            }
        </div>
    );
}
